import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/utils2";

export const AntennaCard = ({ refreshKey = 0 }) => {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const canManage = isRole("president", "tresorier");
  const [st, setSt] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => api.get("/antenna/status").then((r) => setSt(r.data || {})).catch(() => setSt({}));
  useEffect(() => { load(); }, [refreshKey]);

  if (st === null) return null;
  const has = st.contract && st.next_amount;
  if (!has && !canManage) return null;

  return (
    <>
      {has ? (
        <div className="rounded-3xl bg-card border border-border p-5 shadow-ios" data-testid="antenna-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Icons.Radio className="h-4 w-4 text-brand dark:text-primary" /> {t("ant_title")}
            </p>
            {canManage && (
              <button onClick={() => setEditing(true)} data-testid="antenna-edit-btn"
                className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                <Icons.Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <p className="text-2xl font-black">{money(st.next_amount)} {t("currency")}</p>
            <p className="text-sm text-muted-foreground">/ {t("quarter").toLowerCase()} · {st.contract.operator}</p>
          </div>
          <div className={`mt-3 rounded-2xl px-3 py-2 flex items-center justify-between text-sm font-semibold ${st.overdue ? "bg-terracotta/10 text-terracotta" : "bg-accent"}`}
            data-testid="antenna-next-due">
            <span className="flex items-center gap-1.5">
              {st.overdue ? <Icons.AlertTriangle className="h-4 w-4" /> : <Icons.CalendarClock className="h-4 w-4" />}
              {st.overdue ? t("ant_overdue") : t("ant_next_due")}
            </span>
            <span>{fmtDate(st.next_due, lang)}</span>
          </div>
          {st.last_payment && (
            <p className="text-xs text-muted-foreground mt-2">{t("ant_last_payment")} : {fmtDate(st.last_payment, lang)}</p>
          )}
        </div>
      ) : (
        <button onClick={() => setEditing(true)} data-testid="antenna-setup-btn"
          className="w-full rounded-2xl border-2 border-dashed border-brand/40 bg-accent p-4 flex items-center justify-center gap-2 font-semibold text-brand dark:text-primary">
          <Icons.Radio className="h-5 w-5" /> {t("ant_setup")}
        </button>
      )}
      {editing && (
        <AntennaModal contract={st.contract || {}} t={t}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}
    </>
  );
};

function AntennaModal({ contract, onClose, onSaved, t }) {
  const [form, setForm] = useState({
    operator: contract.operator || "Inwi",
    quarterly_amount: contract.quarterly_amount || "",
    start_date: (contract.start_date || "").slice(0, 10),
    end_date: (contract.end_date || "").slice(0, 10),
    contact_name: contract.contact_name || "",
    contact_phone: contract.contact_phone || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!(parseFloat(form.quarterly_amount) > 0) || !form.start_date) { toast.error(t("ant_required")); return; }
    setBusy(true);
    try {
      await api.put("/antenna/contract", {
        ...form,
        quarterly_amount: parseFloat(form.quarterly_amount),
        end_date: form.end_date || null,
      });
      toast.success(t("saved"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="antenna-modal">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-md p-6 shadow-ios-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{t("ant_title")}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <L label={t("ant_operator")}><input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} data-testid="ant-operator" className={field} /></L>
          <L label={t("ant_quarterly")}><input type="number" value={form.quarterly_amount} onChange={(e) => setForm({ ...form, quarterly_amount: e.target.value })} data-testid="ant-amount" className={field} /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label={t("ant_start")}><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ant-start" className={field} /></L>
            <L label={t("ant_end")}><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ant-end" className={field} /></L>
          </div>
          <L label={t("ant_contact")}>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder={t("full_name")} data-testid="ant-contact-name" className={field} />
          </L>
          <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder={t("phone_ph")} data-testid="ant-contact-phone" className={field} />
          <button onClick={save} disabled={busy} data-testid="ant-save"
            className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("save")}</button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
